import React, { useMemo } from 'react';
import { Bookmark, Flag, Check } from 'lucide-react';
import { OptionKey, Question } from '../data/types';

interface QuizQuestionCardProps {
  question: Question;
  pos: number;
  total: number;
  selectedKey?: OptionKey;
  isFlagged: boolean;
  isLocked: boolean;
  isSaving?: boolean;
  isDayCompleted?: boolean;
  isReview?: boolean;
  submittedAt: string | null;
  day: number;
  onSelectOption: (key: OptionKey) => void;
  onToggleFlag: () => void;
}

export const QuizQuestionCard: React.FC<QuizQuestionCardProps> = ({
  question,
  pos,
  selectedKey,
  isFlagged,
  isLocked,
  isSaving = false,
  isDayCompleted = false,
  isReview,
  submittedAt,
  day,
  onSelectOption,
  onToggleFlag,
}) => {
  // Chế độ xem lại khi ngày thi đã hoàn thành hoặc câu hỏi đã được nộp/khóa
  const isReviewMode = isReview ?? (isDayCompleted || isLocked);

  // Tìm điểm cao nhất trong các đáp án của câu hỏi
  const maxOptionScore = useMemo(() => {
    if (!question?.options?.length) return 0;
    return Math.max(...question.options.map((o) => o.score ?? 0));
  }, [question]);

  return (
    <div className="fc-question-card">
      {/* Loading overlay in center of question card when saving answer */}
      {isSaving && (
        <div className="fc-question-loading-overlay">
          <div className="fc-question-spinner" />
          <span className="fc-question-loading-text">Đang lưu câu trả lời...</span>
        </div>
      )}

      <div className="fc-qcard-top">
        <span className="fc-qnum-tag">Câu {pos}</span>
        <span className="fc-competency-pill" title={`Năng lực: ${question.competency}`}>
          Năng lực: <b>{question.competency}</b>
        </span>

        <span className="fc-qcard-spacer" />

        <button
          type="button"
          className={`fc-flag-btn ${isFlagged ? 'flagged' : ''}`}
          onClick={onToggleFlag}
          disabled={isLocked}
        >
          {isFlagged ? <Bookmark size={13} fill="#E0A21B" color="#E0A21B" /> : <Flag size={13} />}
          <span className="fc-flag-text-full">{isFlagged ? '● Đã đánh dấu' : 'Đánh dấu xem lại'}</span>
          <span className="fc-flag-text-short">{isFlagged ? '● Đã lưu' : 'Đánh dấu'}</span>
        </button>
      </div>

      <h2 className="fc-question-text">{question.text}</h2>

      <div className="fc-options-list">
        {question.options.map((option) => {
          const isSelected = selectedKey === option.key;
          const isHighestScore =
            isReviewMode &&
            maxOptionScore > 0 &&
            option.score !== undefined &&
            option.score === maxOptionScore;
          const showScore = isDayCompleted && option.score !== undefined;

          return (
            <div key={option.key} className="fc-option-row">
              <button
                type="button"
                className={`fc-option-btn ${isSelected ? 'selected' : ''} ${
                  isHighestScore ? 'highest-score' : ''
                }`}
                onClick={() => {
                  if (!isLocked) onSelectOption(option.key);
                }}
                disabled={isLocked}
              >
                <span className="fc-option-key">{option.key}</span>
                <span className="fc-option-text">{option.text}</span>
                {isHighestScore && (
                  <span
                    className="fc-option-best-tag"
                    title={`Đáp án cao điểm nhất${option.score ? ` (${option.score} điểm)` : ''}`}
                  >
                    <Check size={16} strokeWidth={2.5} />
                  </span>
                )}
              </button>

              {showScore && (
                <div
                  className={`fc-option-score-outside ${
                    option.score === maxOptionScore
                      ? 'highest'
                      : option.score === 0
                      ? 'zero'
                      : 'normal'
                  }`}
                  title={`Điểm của đáp án ${option.key}: ${option.score} điểm`}
                >
                  <span className="fc-score-num">{option.score}</span>
                  <span className="fc-score-txt">điểm</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
